import { v4 as uuidv4 } from 'uuid';
import { AgentTypes } from '../../constants/event';
import { IAssistCard } from '../../redux/features/assistRequest/assistRequest.props';
import { readEnvVariable } from '..';
import type { IAssistClipStats } from './hearAndConfirm/clip';
import { nextAssistSeq } from './seq';
import {
  AssistAnswerChoice,
  ASSIST_SCHEMA_VERSION,
  IAssistAnswer,
  IAssistEnvelope,
} from './types';

/**
 * HITL-1774 (HITL 8.0.0): `assist_answer`, posted to the event API
 * (`POST /v1/event`, the issue report's path) for the request's store; the
 * events server routes it to gpt-context exactly as a socket send. One per
 * card: the click is final.
 *
 * Never `event: "text"`: an answer is not the agent speaking in the session,
 * and must stay out of the transcript and of every interception check keyed on
 * text events. No trace and no context echo in schema 1.0.
 */

export const ASSIST_ANSWER_EVENT = 'assist_answer';

export interface IAssistAnswerInput {
  card: IAssistCard;
  userId: string;
  answerId: string;
  answerChoice: AssistAnswerChoice;
  /** The words on the clicked box; null for `noise` / `unclear`. */
  answerText?: string | null;
  /** Local epoch ms. */
  answeredAt: number;
  /** The card's clip playback so far; the telemetry fields are left out without it. */
  clipStats?: IAssistClipStats;
}

const sinceCard = (at: number | null, card: IAssistCard) =>
  at === null ? null : Math.round(at - card.receivedAt);

export const buildAssistAnswerEnvelope = ({
  card,
  userId,
  answerId,
  answerChoice,
  answerText = null,
  answeredAt,
  clipStats,
}: IAssistAnswerInput): IAssistEnvelope<IAssistAnswer> => ({
  seq: nextAssistSeq(),
  id: uuidv4(),
  timestamp: new Date(answeredAt).toISOString(),
  session_id: card.sessionId,
  agent_type: AgentTypes.HITL,
  restaurant_code: card.restaurantCode,
  environment: readEnvVariable('DEPLOY_ENV') || 'unknown',
  // Omitted when there is none; never an explicit null (§3).
  ...(card.laneId ? { lane_id: card.laneId } : {}),
  event: ASSIST_ANSWER_EVENT,
  data: {
    schema_version: ASSIST_SCHEMA_VERSION,
    request_id: card.requestId,
    type: card.type,
    agent_id: userId,
    answer_id: answerId,
    answer_choice: answerChoice,
    answer_text: answerText,
    // Both local clocks: the time the card was on screen before the answer.
    decision_ms: Math.max(0, Math.round(answeredAt - card.receivedAt)),
    // Echoed untouched, so gpt-context can tell a card task-router assigned.
    ...(card.assignmentToken ? { assignment_token: card.assignmentToken } : {}),
    // Playback telemetry (optional, additive): also local clocks, from the card.
    ...(clipStats
      ? {
          plays: clipStats.plays,
          first_play_ms: sinceCard(clipStats.firstPlayAt, card),
          autoplayed: clipStats.autoplayed,
          clip_received_ms: sinceCard(clipStats.clipReceivedAt, card),
        }
      : {}),
  },
  metadata: {},
});
